import React from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'react-router-dom';

const CardContainer = styled.div`
  background-color: ${props => props.transparent ? 'transparent' : 'var(--background-light)'};
  border-radius: ${props => props.noBorder ? '0' : 'var(--border-radius)'};
  box-shadow: ${props => props.flat ? 'none' : 'var(--box-shadow)'};
  overflow: hidden;
  transition: all 0.3s ease;
  height: ${props => props.fullHeight ? '100%' : 'auto'};
  display: flex;
  flex-direction: column;
  
  ${props => props.border && css`
    border: 1px solid var(--border-color);
  `}
  
  ${props => props.hoverable && css`
    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
    }
  `}
  
  ${props => props.primary && css`
    background-color: var(--primary-color);
    color: white;
  `}
  
  ${props => props.secondary && css`
    background-color: var(--secondary-color);
    color: var(--text-dark);
  `}
  
  ${props => props.accent && css`
    background-color: var(--accent-color);
    color: white;
  `}
  
  ${props => props.outlined && css`
    background-color: transparent;
    border: 2px solid ${
      props.primary ? 'var(--primary-color)' : 
      props.secondary ? 'var(--secondary-color)' : 
      props.accent ? 'var(--accent-color)' : 
      'var(--border-color)'
    };
  `}
`;

const CardImage = styled.div`
  position: relative;
  width: 100%;
  height: ${props => props.height || '200px'};
  overflow: hidden;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }
  
  ${props => props.hoverable && css`
    ${CardContainer}:hover & img {
      transform: scale(1.05);
    }
  `}
`;

const CardOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${props => props.visible ? 1 : 0};
  transition: opacity 0.3s ease;
  
  ${CardContainer}:hover & {
    opacity: 1;
  }
`;

const CardBody = styled.div`
  padding: ${props => props.tight ? '1rem' : '1.5rem'};
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const CardTitle = styled.h3`
  margin-bottom: 0.75rem;
  color: ${props => 
    (props.primary || props.secondary) ? 'white' : 
    props.accent ? 'var(--text-dark)' : 
    props.light ? 'white' : 
    'var(--text-dark)'
  };
  font-size: ${props => props.small ? '1.25rem' : '1.5rem'};
`;

const CardText = styled.p`
  color: ${props => 
    (props.primary || props.secondary) ? 'rgba(255, 255, 255, 0.85)' : 
    props.accent ? 'var(--text-dark)' : 
    props.light ? 'rgba(255, 255, 255, 0.85)' : 
    'var(--text-light)'
  };
  margin-bottom: 1rem;
`;

const CardFooter = styled.div`
  padding: 1rem 1.5rem;
  border-top: 1px solid ${props => 
    (props.primary || props.secondary) ? 'rgba(255, 255, 255, 0.1)' : 
    'var(--border-color)'
  };
  margin-top: auto;
`;

const LinkWrapper = styled(Link)`
  display: block;
  color: inherit;
  text-decoration: none;
  height: 100%;
  
  ${CardContainer} {
    height: 100%;
  }
`;

const Card = ({ 
  children, 
  image, 
  title, 
  text, 
  footer,
  to,
  imageHeight,
  hoverable = true,
  className = '',
  ...props 
}) => {
  
  const cardContent = (
    <CardContainer hoverable={hoverable} className={className} {...props}>
      {image && (
        <CardImage height={imageHeight} hoverable={hoverable}>
          {typeof image === 'string' ? (
            <img src={image} alt={title || 'Card image'} />
          ) : (
            image
          )}
          {props.overlay && (
            <CardOverlay visible={props.overlayVisible}>
              {props.overlay}
            </CardOverlay>
          )}
        </CardImage>
      )}
      <CardBody tight={props.tight}>
        {title && <CardTitle small={props.smallTitle}>{title}</CardTitle>}
        {text && <CardText>{text}</CardText>}
        {children}
      </CardBody>
      {footer && <CardFooter>{footer}</CardFooter>}
    </CardContainer>
  );
  
  if (to) {
    return <LinkWrapper to={to}>{cardContent}</LinkWrapper>;
  }
  
  return cardContent;
};

export default Card; 